// ---------------------------------------------------------------------------
// The wizard's draft: read from the server, changed by the steps (R17b)
//
// The campaign the wizard edits is one CampaignDraft (wizard/types.ts). This
// module reads a campaign the server sent into a draft (mending old and
// damaged fields on the way), says whether a draft holds anything worth
// keeping, and makes the changes the steps ask for that touch more than one
// field. Taken out of pages/campaign-wizard.ts unchanged. Plain TypeScript:
// every function returns a new draft and changes nothing it is given.
// ---------------------------------------------------------------------------

import type { CampaignDraft, SequenceStep } from '../types.js';
import { defaultLinkedinSequence, deriveCampaignType, emptyCampaignDraft, isPlaceholderSequence } from '../types.js';

/** Parse a time value that may be 'HH:mm' or an ISO string into a Date (today, local time, for 'HH:mm'). */
export const parseTimeValue = (val: unknown): Date => {
  if (typeof val === 'string' && /^\d{1,2}:\d{2}$/.test(val)) {
    const [h, m] = val.split(':').map(Number);
    const d = new Date();
    d.setHours(h!, m!, 0, 0);
    return d;
  }
  const d = new Date(val as string);
  return Number.isNaN(d.getTime()) ? new Date() : d;
};

/**
 * Format a schedule time into a canonical wall-clock "HH:mm" string.
 * Accepts a Date (from the time picker, in browser-local wall-clock terms) or a
 * legacy "HH:mm"/ISO string. Reading the local hours preserves exactly what the
 * user typed; the value is interpreted in the campaign's IANA timezone
 * (DST-aware) downstream. We must NOT let JSON.stringify emit a UTC ISO string,
 * which would bake in the browser's offset and shift the send window.
 */
export const formatTimeHHmm = (val: Date | string): string => {
  const d = typeof val === 'string' ? parseTimeValue(val) : val;
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/**
 * The one step a new draft starts with, untouched: the email placeholder every
 * draft opens with (isPlaceholderSequence), or the LinkedIn step the wizard
 * puts there instead for an account with LinkedIn and no mailbox
 * (withLinkedinPlaceholder). Either is the wizard's guess, not the person's.
 */
export function isWizardPlaceholder(sequence: SequenceStep[]): boolean {
  if (isPlaceholderSequence(sequence)) return true;
  const [placeholder] = defaultLinkedinSequence();
  const [step, ...rest] = sequence;
  if (!placeholder || !step || rest.length > 0) return false;
  return (
    step.channel === placeholder.channel &&
    step.condition === placeholder.condition &&
    step.prompt === placeholder.prompt &&
    (step.subject ?? '') === placeholder.subject
  );
}

/**
 * Whether the user has entered any meaningful data in the wizard (the leave
 * hook asks "Save draft?" then). The type and the placeholder step every new
 * draft starts with are not data (R17a3): counted, they made a wizard opened
 * and left at once ask too, and Save then created an empty
 * "Draft - Untitled - <date>" (found in R17b). Everything `hasSaveworthyData`
 * counts is counted here too: the placeholder made this always true, which hid
 * that talking points, reps and LinkedIn accounts were missing from it.
 */
export const hasMeaningfulData = (draft: CampaignDraft): boolean =>
  !!(
    draft.name ||
    draft.offering.name ||
    draft.offering.description ||
    draft.offering.talkingPoints.length > 0 ||
    draft.leads.length > 0 ||
    (draft.sequence.length > 0 && !isWizardPlaceholder(draft.sequence)) ||
    draft.aiEmployeeAssignments.length > 0 ||
    draft.emailAccounts.length > 0 ||
    draft.linkedinAccountIds.length > 0 ||
    draft.apolloSearchInput
  );

/**
 * Stricter than `hasMeaningfulData`: a brand-new draft already has a default
 * sequence + campaignType, which we do NOT want to persist on its own. Only
 * real user input (name, leads, offering, accounts, …) makes a draft worth
 * auto-creating in the database.
 */
export const hasSaveworthyData = (draft: CampaignDraft): boolean =>
  !!(
    draft.name.trim() ||
    draft.leads.length > 0 ||
    draft.offering.name.trim() ||
    draft.offering.description.trim() ||
    draft.offering.talkingPoints.length > 0 ||
    draft.aiEmployeeAssignments.length > 0 ||
    draft.emailAccounts.length > 0 ||
    draft.linkedinAccountIds.length > 0 ||
    draft.apolloSearchInput
  );

/** A campaign read for editing: the draft, and what the server already holds of it. */
export interface LoadedCampaign {
  draft: CampaignDraft;
  /** The ids of the steps that already exist server-side (with messages, past 'draft'). */
  stepIds: Set<string>;
  /** The Sales Rep assignments and per-rep accounts as the server holds them (the base the edits are diffed on). */
  appliedAssignments: CampaignDraft['aiEmployeeAssignments'];
  appliedEmailAccountsByAie: Record<string, string[]>;
  appliedLinkedinAccountIdsByAie: Record<string, string[]>;
}

/**
 * Reads GET /api/campaigns/:id's campaign (and its leads) into a draft.
 *
 * The `?? x.doc?.Y` fallbacks recover campaigns whose llmModel /
 * aiEmployeeAssignments / emailAccountsByAie / linkedinAccountIdsByAie were
 * corrupted by a since-fixed server-side bug (an entity instance written
 * straight through a raw MongoDB updateOne serializes its non-enumerable
 * internals instead of its real fields, burying them at e.g.
 * `a.doc.aiEmployeeId`) — without this, an older affected campaign shows no
 * Sales Rep selected here even though one was actually chosen.
 */
// biome-ignore lint/suspicious/noExplicitAny: the server's campaign document, read field by field
export function draftFromCampaign(c: any, leads: any[]): LoadedCampaign {
  const loadedSequence = (c.sequence ?? []).map((s: any) => ({
    id: s.stepId ?? s.id,
    channel: s.channel,
    delayValue: s.delayValue,
    delayUnit: s.delayUnit,
    condition: s.condition,
    prompt: s.prompt,
    subject: s.subject,
    subjectPrompt: s.subjectPrompt ?? '',
    manualBody: s.manualBody ?? undefined,
    previewSubject: s.previewSubject ?? undefined,
    previewBody: s.previewBody ?? undefined,
    previewKey: s.previewKey ?? undefined,
    refineInstruction: s.refineInstruction ?? undefined,
  }));
  const loadedAssignments: CampaignDraft['aiEmployeeAssignments'] =
    Array.isArray(c.aiEmployeeAssignments) && c.aiEmployeeAssignments.length > 0
      ? c.aiEmployeeAssignments.map((a: any) => ({
          aiEmployeeId: a.aiEmployeeId ?? a.doc?.aiEmployeeId,
          percentage: Number(a.percentage ?? a.doc?.percentage) || 0,
        }))
      : c.aiEmployeeId
        ? [{ aiEmployeeId: c.aiEmployeeId, percentage: 100 }]
        : [];
  const loadedEmailByAie: Record<string, string[]> = Array.isArray(c.emailAccountsByAie)
    ? Object.fromEntries(
        c.emailAccountsByAie.map((b: any) => [
          b.aiEmployeeId ?? b.doc?.aiEmployeeId,
          b.emailAccounts ?? b.doc?.emailAccounts ?? [],
        ]),
      )
    : c.aiEmployeeId && (c.emailAccounts ?? []).length > 0
      ? { [c.aiEmployeeId]: c.emailAccounts }
      : {};
  const loadedLinkedinByAie: Record<string, string[]> = Array.isArray(c.linkedinAccountIdsByAie)
    ? Object.fromEntries(
        c.linkedinAccountIdsByAie.map((b: any) => [
          b.aiEmployeeId ?? b.doc?.aiEmployeeId,
          b.linkedinAccountIds ?? b.doc?.linkedinAccountIds ?? [],
        ]),
      )
    : c.aiEmployeeId && (c.linkedinAccountIds ?? []).length > 0
      ? { [c.aiEmployeeId]: c.linkedinAccountIds }
      : {};
  const draft: CampaignDraft = {
    _id: String(c._id),
    externalCampaignId: c.externalCampaignId ?? undefined,
    status: c.status,
    launchedAt: c.launchedAt ?? undefined,
    name: c.name ?? '',
    campaignType: c.campaignType || null,
    offering: c.offering ?? { name: '', description: '', talkingPoints: [] },
    leads,
    schedule: c.schedule
      ? { ...c.schedule, startTime: parseTimeValue(c.schedule.startTime), endTime: parseTimeValue(c.schedule.endTime) }
      : emptyCampaignDraft().schedule,
    sequence: loadedSequence,
    emailAccounts: c.emailAccounts ?? [],
    linkedinAccountIds: c.linkedinAccountIds ?? (c.linkedinAccountId ? [c.linkedinAccountId] : []),
    llmModel: c.llmModel
      ? {
          id: c.llmModel.llmId ?? c.llmModel.id ?? c.llmModel.doc?.llmId ?? c.llmModel.doc?.id,
          model: c.llmModel.model ?? c.llmModel.doc?.model,
          label: c.llmModel.label ?? c.llmModel.doc?.label,
        }
      : null,
    aiEmployeeId: c.aiEmployeeId ?? null,
    aiEmployeeAssignments: loadedAssignments,
    emailAccountsByAie: loadedEmailByAie,
    linkedinAccountIdsByAie: loadedLinkedinByAie,
    channelFallbacks: Array.isArray(c.channelFallbacks)
      ? c.channelFallbacks.map((f: any) => ({
          sourceAiEmployeeId: f.sourceAiEmployeeId,
          channel: f.channel,
          fallbackAiEmployeeId: f.fallbackAiEmployeeId,
        }))
      : [],
    autoReply: c.autoReply ?? false,
    replyPrompt: c.replyPrompt ?? '',
    crmAutoSync: c.crmAutoSync ?? false,
    language: c.language ?? 'en',
    replyLlmModel: c.replyLlmModel
      ? { id: c.replyLlmModel.llmId ?? c.replyLlmModel.id, model: c.replyLlmModel.model, label: c.replyLlmModel.label }
      : null,
    apolloSearchInput: c.apolloSearchInputJson ? JSON.parse(c.apolloSearchInputJson) : undefined,
    projectId: c.projectId ?? undefined,
  };
  return {
    draft,
    // Every step already loaded from the server already has messages generated
    // for it (or is legitimately still 'draft', where the edit-mode
    // restrictions never apply anyway) — the ids tell them apart from steps
    // added in this visit, which aren't confirmed/generated until their
    // debounce fires.
    stepIds: new Set(loadedSequence.map((s: { id: string }) => s.id)),
    appliedAssignments: loadedAssignments.map(a => ({ ...a })),
    appliedEmailAccountsByAie: structuredClone(loadedEmailByAie),
    appliedLinkedinAccountIdsByAie: structuredClone(loadedLinkedinByAie),
  };
}

/** Per-rep account buckets that a rep-set change seeds for a rep just ticked (their one account per channel). */
export interface AccountSeed {
  emailAccountsByAie?: Record<string, string[]>;
  linkedinAccountIdsByAie?: Record<string, string[]>;
}

/**
 * A new set of Sales Rep assignments: the per-rep account buckets of reps no
 * longer assigned go (in the same change, so a later setter cannot spread a
 * stale draft over it), the seeded buckets come in, and the flat lists and the
 * legacy single rep follow.
 */
export function withAssignments(
  draft: CampaignDraft,
  aiEmployeeAssignments: CampaignDraft['aiEmployeeAssignments'],
  seed?: AccountSeed,
): CampaignDraft {
  const validIds = new Set(aiEmployeeAssignments.map(a => a.aiEmployeeId));
  const emailAccountsByAie = Object.fromEntries(
    Object.entries({ ...draft.emailAccountsByAie, ...(seed?.emailAccountsByAie ?? {}) }).filter(([id]) =>
      validIds.has(id),
    ),
  );
  const linkedinAccountIdsByAie = Object.fromEntries(
    Object.entries({ ...draft.linkedinAccountIdsByAie, ...(seed?.linkedinAccountIdsByAie ?? {}) }).filter(([id]) =>
      validIds.has(id),
    ),
  );
  return {
    ...draft,
    aiEmployeeAssignments,
    // Mirror legacy single-AIE field for backward compat in API + checks.
    aiEmployeeId: aiEmployeeAssignments[0]?.aiEmployeeId ?? null,
    emailAccountsByAie,
    linkedinAccountIdsByAie,
    emailAccounts: Array.from(new Set(Object.values(emailAccountsByAie).flat())),
    linkedinAccountIds: Array.from(new Set(Object.values(linkedinAccountIdsByAie).flat())),
  };
}

/** The mailboxes picked per rep; the flat list is their union (used by legacy validators and the API). */
export const withEmailAccountsByAie = (
  draft: CampaignDraft,
  emailAccountsByAie: Record<string, string[]>,
): CampaignDraft => ({
  ...draft,
  emailAccountsByAie,
  emailAccounts: Array.from(new Set(Object.values(emailAccountsByAie).flat())),
});

/** The LinkedIn accounts picked per rep; the flat list is their union. */
export const withLinkedinAccountIdsByAie = (
  draft: CampaignDraft,
  linkedinAccountIdsByAie: Record<string, string[]>,
): CampaignDraft => ({
  ...draft,
  linkedinAccountIdsByAie,
  linkedinAccountIds: Array.from(new Set(Object.values(linkedinAccountIdsByAie).flat())),
});

/** A new sequence; the campaign type follows it (the user no longer picks one). */
export const withSequence = (draft: CampaignDraft, sequence: SequenceStep[]): CampaignDraft => ({
  ...draft,
  sequence,
  campaignType: deriveCampaignType(sequence),
});

/**
 * A new outreach language, in ONE update: the language, and every step's stored
 * AI preview dropped (it is in the old language; the cards generate it anew).
 */
export const withLanguage = (draft: CampaignDraft, language: string): CampaignDraft => ({
  ...draft,
  language,
  sequence: draft.sequence.map(s => ({
    ...s,
    previewSubject: undefined,
    previewBody: undefined,
    previewKey: undefined,
  })),
});

/** The steps whose hand-written message a language switch translates (manual copy is sent verbatim). */
export const manualSteps = (draft: CampaignDraft): SequenceStep[] => draft.sequence.filter(s => s.manualBody?.trim());

/** One manual step's message replaced by its translation (the subject only when one came back). */
export const withTranslatedStep = (
  draft: CampaignDraft,
  stepId: string,
  translation: { body: string; subject?: string },
): CampaignDraft => ({
  ...draft,
  sequence: draft.sequence.map(s =>
    s.id === stepId ? { ...s, manualBody: translation.body, subject: translation.subject ?? s.subject } : s,
  ),
});

/** A list of the user's accounts as GET /api/accounts/* answers it, or a failure. */
type AccountsAnswer = { ok?: boolean; accounts?: unknown[] };

/**
 * Whether a brand-new draft's placeholder step should move to LinkedIn: the
 * user has a LinkedIn account and certainly no mailbox. Every new draft opens
 * with one EMAIL step, and the Sales Rep step derives the channels a rep needs
 * an account for from exactly that sequence; someone who only connected
 * LinkedIn was asked for a mailbox they don't have. Email accounts come from
 * two sources (Instantly, retiring, and Unipile); a failed lookup means we
 * cannot be sure, so the placeholder stays.
 */
export function placeholderGoesToLinkedin(
  linkedin: AccountsAnswer,
  email: AccountsAnswer,
  unipileEmail: AccountsAnswer,
): boolean {
  if (!linkedin.ok || (linkedin.accounts ?? []).length === 0) return false;
  if (!email.ok || !unipileEmail.ok) return false;
  return (email.accounts ?? []).length === 0 && (unipileEmail.accounts ?? []).length === 0;
}

/** Whether the LinkedIn answer alone already says the placeholder stays (the mailbox lookup is then skipped). */
export const noLinkedinToMoveTo = (linkedin: AccountsAnswer): boolean =>
  !linkedin.ok || (linkedin.accounts ?? []).length === 0;

/**
 * The draft with its placeholder step on LinkedIn, or null when the user has
 * shaped the sequence meanwhile (only the untouched placeholder is replaced).
 */
export function withLinkedinPlaceholder(draft: CampaignDraft): CampaignDraft | null {
  if (!isPlaceholderSequence(draft.sequence)) return null;
  const sequence = defaultLinkedinSequence().slice(0, 1);
  return { ...draft, sequence, campaignType: deriveCampaignType(sequence) };
}
